import { FeedbackOrigin } from '../../../../components/ai/DisclosureControls';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getAvailableAudio, getAudioStreamUrl } from './audioApi';
import {
  wordIndexAtMs,
  createListenTracker,
  sentenceAnchorAtMs,
  anchorForMs,
  playbackAnchorAtMs,
  startMsForTextPosition,
} from './audioSync';
import * as tracker from 'src/utils/tracker';
import './chapterAudioPlayer.css';

/**
 * Per-chapter listening player (Phase 2 + 3). Streams a ready AudioRendition
 * chapter from a short-TTL signed URL, shows a karaoke read-along strip driven
 * by `timing.words`, and emits the SAME tracker events the text reader does so
 * "listened" progress fills the same reader-progress fields (plan §7.3).
 *
 * Phase 3 — comment & react WHILE LISTENING: a one-tap reaction row and a
 * "Comment here" affordance that AUTO-PAUSES playback, resolves the current
 * SENTENCE anchor from `timing.sentences` (§6.2), and hands `{kind, text,
 * reaction, inlineInfo}` to `onSubmitComment` — which the reader wires to the
 * EXISTING inline-comment thunk, so it lands like any inline comment (§6.3). No
 * sentence anchor (missing timing) → degrades to a chapter-level comment (§6.4).
 *
 * Robust by design: renders nothing unless the chapter has ready audio; if the
 * timing map is absent it degrades to plain `<audio>` playback (no karaoke, no
 * crash). The parent gates mounting on the `audiobook` feature flag;
 * `enabled={false}` also short-circuits here.
 *
 * `track` and `onSubmitComment` are injected (defaults: real tracker / no-op)
 * for testability.
 */
// one-tap reaction bar — the EXACT existing Comment.reaction enum + emojis
const REACTIONS = [
  { reaction: 'LIKE', emoji: '👍' },
  { reaction: 'LOVE', emoji: '❤️' },
  { reaction: 'LAUGH', emoji: '😂' },
  { reaction: 'SURPRISE', emoji: '😮' },
  { reaction: 'ANGRY', emoji: '😠' },
  { reaction: 'SAD', emoji: '😢' },
];
const reactionEmoji = reaction =>
  REACTIONS.find(option => option.reaction === reaction)?.emoji || reaction;
const feedbackTargetKey = (mode, anchor) => {
  if (!anchor) return `${mode}:chapter`;
  return [
    mode,
    anchor.sceneId || '',
    anchor.startKey,
    anchor.startOffset,
    anchor.endKey,
    anchor.endOffset,
  ].join(':');
};
const formatTime = milliseconds => {
  const totalSeconds = Math.max(0, Math.floor((milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
};
const NOOP = () => {};
const READ_HIGHLIGHT_HOLD_MS = 1000;
const READ_HIGHLIGHT_FADE_MS = 400;
const readyContinuationQueue = (queue, readyPartIds) => {
  const ready = new Set(readyPartIds.map(String));
  const result = [];
  for (const item of queue) {
    if (!ready.has(String(item.partId))) break;
    result.push(item);
  }
  return result;
};
const buildRows = (events, { bookId, chapterId, userId }) =>
  events.map(e => ({
    event: { id: e.id, count: e.count, timestamp: Date.now() },
    user: { id: userId },
    book: { id: bookId },
    chapter: { id: chapterId },
    // soft modality tag so analytics can split listen-vs-read (plan §4.3)
    modality: 'audio',
  }));

const ChapterAudioPlayer = ({
  bookId,
  contentVersionId,
  chapter,
  userId,
  idToken,
  nativeAudioBridge = null,
  nativeContinuationQueue = [],
  bookTitle,
  authorName,
  colorMode = 'bright',
  enabled = true,
  track = tracker.track,
  onSubmitComment = NOOP,
  autoStart = false,
  onAutoStartHandled = NOOP,
  onPlaybackStateChange = NOOP,
  onPlaybackAnchorChange = NOOP,
  onEnded = NOOP,
  // Listen ⇄ Read toggle (§7.5). getTextPosition() → the reader's current text
  // position {key, offset} (top-of-viewport sentence); orderedKeys = the
  // chapter's anchor keys in document order; onSwitchToRead(anchor) tells the
  // reader to scroll+highlight the sentence being heard. All optional — absent
  // → the toggle degrades (starts at chapter start / no read-jump).
  getTextPosition = null,
  orderedKeys = [],
  onSwitchToRead = null,
  readingFeedbackSelection = null,
  onViewModeChange = NOOP,
  onDockHeightChange = NOOP,
}) => {
  const chapterId = chapter && chapter._id;
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState('idle'); // idle|loading|ready|error|unavailable
  const [stream, setStream] = useState(null); // {url, durationMs, timing}
  const [wordIndex, setWordIndex] = useState(-1);
  const [composerOpen, setComposerOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [feedbackOrigin, setFeedbackOrigin] = useState('undeclared');
  const [recentReactions, setRecentReactions] = useState([]);
  const [viewMode, setViewMode] = useState('reading');
  const [availabilityResolved, setAvailabilityResolved] = useState(false);
  const [readyPartIds, setReadyPartIds] = useState([]);
  const commentMsRef = useRef(0); // audioMs captured at the moment of the tap
  const wasPlayingRef = useRef(false); // was audio playing when the composer opened
  const startAtMsRef = useRef(0); // Read→Listen: where to begin playback
  const requestedStartPositionRef = useRef(null);
  const autoStartHandledRef = useRef(false);
  const nativeRequestId = `${bookId}:${contentVersionId}:${chapterId}`;
  const nativePlaybackEnabled = Boolean(nativeAudioBridge?.sendMessage);
  const nativeSendRef = useRef(nativeAudioBridge?.sendMessage);
  nativeSendRef.current = nativeAudioBridge?.sendMessage;
  const onViewModeChangeRef = useRef(onViewModeChange);
  onViewModeChangeRef.current = onViewModeChange;
  const nativeCurrentMsRef = useRef(0);
  const nativePlayingRef = useRef(false);
  const nativeRecoveryRequestedRef = useRef(false);
  const lastNativeEventRef = useRef(null);
  const lastPlaybackAnchorRef = useRef('');
  const lastPlaybackAnchorValueRef = useRef(null);
  const highlightFadeTimersRef = useRef([]);
  const [nativePlayback, setNativePlayback] = useState({
    currentMs: 0,
    durationMs: 0,
    isPlaying: false,
  });
  const [browserPlayback, setBrowserPlayback] = useState({
    currentMs: 0,
    durationMs: 0,
    isPlaying: false,
  });

  const audioRef = useRef(null);
  const dockRef = useRef(null);
  const onDockHeightChangeRef = useRef(onDockHeightChange);
  onDockHeightChangeRef.current = onDockHeightChange;
  const showBottomDock =
    (available && (state === 'idle' || state === 'loading')) ||
    (state === 'ready' && Boolean(stream));

  useEffect(() => {
    if (!showBottomDock || !dockRef.current) {
      onDockHeightChangeRef.current(0);
      return undefined;
    }

    const dock = dockRef.current;
    const updateHeight = () => onDockHeightChangeRef.current(dock.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(dock);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [showBottomDock, viewMode, readingFeedbackSelection, composerOpen]);

  useEffect(() => () => onDockHeightChangeRef.current(0), []);
  const listenTrackerRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      onPlaybackStateChange(false);
    };
  }, [onPlaybackStateChange]);

  const clearHighlightFadeTimers = useCallback(() => {
    highlightFadeTimersRef.current.forEach(timer => clearTimeout(timer));
    highlightFadeTimersRef.current = [];
  }, []);

  const fadePlaybackHighlightForReading = useCallback(() => {
    clearHighlightFadeTimers();
    const activeAnchor = lastPlaybackAnchorValueRef.current;
    if (!activeAnchor) {
      onPlaybackAnchorChange(null);
      return;
    }
    const fadeTimer = setTimeout(() => {
      onPlaybackAnchorChange({ ...activeAnchor, fading: true });
      const clearTimer = setTimeout(() => {
        onPlaybackAnchorChange(null);
        lastPlaybackAnchorRef.current = '';
        lastPlaybackAnchorValueRef.current = null;
      }, READ_HIGHLIGHT_FADE_MS);
      highlightFadeTimersRef.current.push(clearTimer);
    }, READ_HIGHLIGHT_HOLD_MS);
    highlightFadeTimersRef.current.push(fadeTimer);
  }, [clearHighlightFadeTimers, onPlaybackAnchorChange]);

  useEffect(
    () => () => {
      clearHighlightFadeTimers();
    },
    [clearHighlightFadeTimers]
  );

  const sendNative = useCallback(
    (type, data = {}) => {
      nativeSendRef.current?.({
        type,
        data: { requestId: nativeRequestId, ...data },
      });
    },
    [nativeRequestId]
  );

  const getPreferredTextPosition = useCallback(() => {
    const selectedAnchor = readingFeedbackSelection?.anchor;
    if (selectedAnchor?.startKey != null) {
      return {
        key: selectedAnchor.startKey,
        offset: selectedAnchor.startOffset ?? 0,
      };
    }
    return getTextPosition ? getTextPosition() : null;
  }, [getTextPosition, readingFeedbackSelection]);

  const captureRequestedStartPosition = useCallback(() => {
    requestedStartPositionRef.current = getPreferredTextPosition();
  }, [getPreferredTextPosition]);

  useEffect(
    () => () => {
      if (nativePlaybackEnabled) sendNative('AUDIO_RELEASE');
    },
    [nativePlaybackEnabled, sendNative]
  );

  // Discover whether THIS chapter has ready audio (cheap; no URL signing).
  useEffect(() => {
    let cancelled = false;
    if (!enabled || !chapterId || !idToken) return undefined;
    setAvailable(false);
    setAvailabilityResolved(false);
    setReadyPartIds([]);
    getAvailableAudio(idToken, bookId, contentVersionId)
      .then(res => {
        if (cancelled) return;
        const resolvedReadyPartIds = (res.chapters || []).map(c => c.partId);
        const ready = (res.chapters || []).some(
          c => String(c.partId) === String(chapterId)
        );
        setReadyPartIds(resolvedReadyPartIds);
        setAvailable(ready);
        setAvailabilityResolved(true);
        if (ready) onViewModeChangeRef.current('reading');
      })
      .catch(() => {
        if (!cancelled) {
          setAvailable(false);
          setAvailabilityResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, bookId, contentVersionId, chapterId, idToken]);

  useEffect(() => {
    autoStartHandledRef.current = false;
  }, [chapterId, autoStart]);

  const emit = useCallback(
    events => {
      if (!events || !events.length) return;
      try {
        track({
          book: bookId,
          data: buildRows(events, { bookId, chapterId, userId }),
        });
      } catch (e) {
        /* telemetry must never break playback */
      }
    },
    [track, bookId, chapterId, userId]
  );

  const startListening = useCallback(async () => {
    clearHighlightFadeTimers();
    if (!requestedStartPositionRef.current) {
      captureRequestedStartPosition();
    }
    setState('loading');
    if (nativePlaybackEnabled) {
      sendNative('AUDIO_LOAD', {
        bookId,
        contentVersionId,
        partId: chapterId,
        title: chapter?.title,
        bookTitle,
        authorName,
        continuationQueue: readyContinuationQueue(
          nativeContinuationQueue,
          readyPartIds
        ),
      });
      return;
    }
    try {
      const data = await getAudioStreamUrl(
        idToken,
        bookId,
        contentVersionId,
        chapterId
      );
      if (!mountedRef.current) return; // chapter changed mid-fetch
      setStream(data);
      listenTrackerRef.current = createListenTracker({
        durationMs: data.durationMs || 0,
      });
      // Read → Listen continuity (§7.5): begin playback at the selected word
      // when timing permits, otherwise the sentence/reader position. A null
      // timing result degrades to chapter start.
      const sentences = data.timing && data.timing.sentences;
      const pos =
        requestedStartPositionRef.current || getPreferredTextPosition();
      requestedStartPositionRef.current = null;
      // orderedKeys may be an array or a function (reader reads the DOM fresh)
      const keys =
        typeof orderedKeys === 'function' ? orderedKeys() : orderedKeys || [];
      const startMs = startMsForTextPosition(
        sentences || [],
        pos,
        keys,
        data.timing?.words || []
      );
      startAtMsRef.current = startMs || 0;
      setViewMode('listening');
      onViewModeChangeRef.current('listening');
      setState('ready');
    } catch (err) {
      if (!mountedRef.current) return;
      setState(err && err.code === 'NOT_READY' ? 'unavailable' : 'error');
    }
  }, [
    idToken,
    bookId,
    bookTitle,
    authorName,
    chapter,
    contentVersionId,
    chapterId,
    nativePlaybackEnabled,
    nativeContinuationQueue,
    readyPartIds,
    sendNative,
    clearHighlightFadeTimers,
    getPreferredTextPosition,
    captureRequestedStartPosition,
  ]);

  useEffect(() => {
    if (
      !autoStart ||
      !availabilityResolved ||
      state !== 'idle' ||
      autoStartHandledRef.current
    ) {
      return;
    }

    autoStartHandledRef.current = true;
    onAutoStartHandled(available);
    if (available) startListening();
  }, [
    autoStart,
    availabilityResolved,
    available,
    state,
    startListening,
    onAutoStartHandled,
  ]);

  const updatePlaybackPosition = useCallback(
    ms => {
      // karaoke highlight (only when we have a timing map)
      const words = stream && stream.timing && stream.timing.words;
      if (words && words.length) {
        const idx = wordIndexAtMs(words, ms);
        setWordIndex(prev => (prev === idx ? prev : idx));
      }
      const playbackAnchor = playbackAnchorAtMs(stream?.timing, ms);
      const playbackAnchorKey = playbackAnchor
        ? [
            playbackAnchor.sceneId || '',
            playbackAnchor.startKey,
            playbackAnchor.startOffset,
            playbackAnchor.endKey,
            playbackAnchor.endOffset,
          ].join(':')
        : '';
      if (playbackAnchorKey !== lastPlaybackAnchorRef.current) {
        lastPlaybackAnchorRef.current = playbackAnchorKey;
        lastPlaybackAnchorValueRef.current = playbackAnchor;
        onPlaybackAnchorChange(playbackAnchor);
      }
      // telemetry from playback position
      if (listenTrackerRef.current) {
        emit(listenTrackerRef.current.advance(ms));
      }
    },
    [stream, emit, onPlaybackAnchorChange]
  );

  useEffect(
    () => () => {
      onPlaybackAnchorChange(null);
    },
    [onPlaybackAnchorChange]
  );

  const handleTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const currentMs = el.currentTime * 1000;
    setBrowserPlayback(previous => ({ ...previous, currentMs }));
    updatePlaybackPosition(currentMs);
  }, [updatePlaybackPosition]);

  useEffect(() => {
    const event = nativeAudioBridge?.event;
    if (
      !nativePlaybackEnabled ||
      !event ||
      event === lastNativeEventRef.current ||
      event.data?.requestId !== nativeRequestId
    ) {
      return;
    }
    lastNativeEventRef.current = event;

    if (event.type === 'AUDIO_READY') {
      const recoveredFromNativeState = nativeRecoveryRequestedRef.current;
      nativeRecoveryRequestedRef.current = false;
      const data = {
        durationMs: event.data.durationMs || 0,
        timing: event.data.timing || null,
        voice: event.data.voice,
        native: true,
      };
      setStream(data);
      listenTrackerRef.current = createListenTracker({
        durationMs: data.durationMs,
      });
      if (recoveredFromNativeState) {
        setNativePlayback(previous => ({
          ...previous,
          durationMs: data.durationMs || previous.durationMs,
        }));
        setState('ready');
        return;
      }
      setViewMode('listening');
      onViewModeChangeRef.current('listening');
      const sentences = data.timing?.sentences || [];
      const pos =
        requestedStartPositionRef.current || getPreferredTextPosition();
      requestedStartPositionRef.current = null;
      const keys =
        typeof orderedKeys === 'function' ? orderedKeys() : orderedKeys || [];
      const startMs =
        startMsForTextPosition(
          sentences,
          pos,
          keys,
          data.timing?.words || []
        ) || 0;
      nativeCurrentMsRef.current = startMs;
      setNativePlayback({
        currentMs: startMs,
        durationMs: data.durationMs,
        isPlaying: false,
      });
      setState('ready');
      if (startMs > 0) sendNative('AUDIO_SEEK', { positionMs: startMs });
      sendNative('AUDIO_PLAY');
      return;
    }

    if (event.type === 'AUDIO_STATE') {
      const currentMs = event.data.currentMs || 0;
      const isPlaying = Boolean(event.data.isPlaying);
      setStream(current => {
        if (current) {
          if (!current.timing && event.data.timing) {
            return {
              ...current,
              durationMs: event.data.durationMs || current.durationMs || 0,
              timing: event.data.timing,
              voice: event.data.voice || current.voice,
            };
          }
          return current;
        }
        return {
          durationMs: event.data.durationMs || 0,
          timing: event.data.timing || null,
          voice: event.data.voice,
          native: true,
        };
      });
      if (state !== 'ready' || !stream) {
        setViewMode('listening');
        onViewModeChangeRef.current('listening');
        if (!nativeRecoveryRequestedRef.current) {
          nativeRecoveryRequestedRef.current = true;
          sendNative('AUDIO_SYNC');
        }
        if (!listenTrackerRef.current) {
          listenTrackerRef.current = createListenTracker({
            durationMs: event.data.durationMs || 0,
          });
        }
        setState('ready');
      }
      nativeCurrentMsRef.current = currentMs;
      nativePlayingRef.current = isPlaying;
      setNativePlayback({
        currentMs,
        durationMs: event.data.durationMs || stream?.durationMs || 0,
        isPlaying,
      });
      updatePlaybackPosition(currentMs);
      onPlaybackStateChange(isPlaying);
      return;
    }

    if (event.type === 'AUDIO_ENDED') {
      nativePlayingRef.current = false;
      onPlaybackStateChange(false);
      if (listenTrackerRef.current) {
        emit(listenTrackerRef.current.markEnded());
      }
      onEnded();
      return;
    }

    if (event.type === 'AUDIO_ERROR') {
      nativePlayingRef.current = false;
      onPlaybackStateChange(false);
      setState('error');
    }
  }, [
    nativeAudioBridge?.event,
    nativePlaybackEnabled,
    nativeRequestId,
    getPreferredTextPosition,
    orderedKeys,
    onEnded,
    onPlaybackStateChange,
    emit,
    sendNative,
    state,
    stream,
    stream?.durationMs,
    updatePlaybackPosition,
  ]);

  const currentMs = () =>
    nativePlaybackEnabled
      ? nativeCurrentMsRef.current
      : audioRef.current
        ? audioRef.current.currentTime * 1000
        : 0;

  // Build the feedback payload at a given playback position: resolve the
  // SENTENCE anchor from timing.sentences; when there's no usable timing,
  // degrade to a chapter-level comment (§6.4). Shape matches the existing
  // inline-comment thunk (kind/text/reaction/inlineInfo).
  const buildFeedback = (ms, { text, reaction }, anchorOverride = null) => {
    const sentences = stream && stream.timing && stream.timing.sentences;
    const inlineInfo =
      anchorOverride || sentenceAnchorAtMs(sentences || [], ms);
    if (inlineInfo) {
      return { kind: 'inline', text, reaction, inlineInfo };
    }
    return { kind: 'chapter', text, reaction };
  };

  const submitFeedback = payload => {
    try {
      onSubmitComment(payload);
    } catch (e) {
      /* submit errors are surfaced by the reader's thunk, never crash playback */
    }
  };

  // One-tap reaction — fires at the current position; no pause needed (§6.5).
  const handleReaction = reaction => {
    const reactionMs = currentMs();
    const reactionAnchor =
      viewMode === 'reading'
        ? readingFeedbackSelection?.anchor
        : sentenceAnchorAtMs(stream?.timing?.sentences || [], reactionMs);
    const targetKey = feedbackTargetKey(viewMode, reactionAnchor);
    setRecentReactions(previous => [
      ...previous.slice(-2),
      {
        reaction,
        emoji: reactionEmoji(reaction),
        audioMs: reactionMs,
        targetKey,
      },
    ]);
    submitFeedback(
      buildFeedback(
        reactionMs,
        { reaction },
        viewMode === 'reading' ? readingFeedbackSelection?.anchor : null
      )
    );
  };

  // "Comment here" — auto-pause and capture the audioMs at the moment of the
  // tap (not when typing finishes, §6.1), then open the composer. Remember
  // whether audio was playing so we only RESUME what we paused (don't start
  // audio the listener had deliberately paused).
  const openComposer = () => {
    const el = audioRef.current;
    const isListening = viewMode === 'listening';
    wasPlayingRef.current = isListening
      ? nativePlaybackEnabled
        ? nativePlayingRef.current
        : !!(el && !el.paused)
      : false;
    if (isListening) {
      if (nativePlaybackEnabled) sendNative('AUDIO_PAUSE');
      else if (el) el.pause();
    }
    commentMsRef.current = currentMs();
    setFeedbackOrigin('undeclared');
    setComposerOpen(true);
  };
  const closeComposer = () => {
    setComposerOpen(false);
    setCommentText('');
    if (viewMode === 'listening' && wasPlayingRef.current) {
      if (nativePlaybackEnabled) sendNative('AUDIO_PLAY');
      else if (audioRef.current) audioRef.current.play().catch(() => {});
    }
  };
  const submitComposer = () => {
    const text = commentText.trim();
    if (!text || feedbackOrigin !== 'personal') return;
    submitFeedback({
      aiOrigin: 'personal',
      ...buildFeedback(
        commentMsRef.current,
        { text },
        viewMode === 'reading' ? readingFeedbackSelection?.anchor : null
      )
    });
    closeComposer();
  };

  // Once the audio is loaded, seek to the Read→Listen start position and play.
  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    setBrowserPlayback(previous => ({
      ...previous,
      durationMs:
        stream?.durationMs ||
        (Number.isFinite(el.duration) ? el.duration * 1000 : 0),
    }));
    if (startAtMsRef.current > 0) {
      try {
        el.currentTime = startAtMsRef.current / 1000;
      } catch (e) {
        /* seeking may throw before the media is seekable — ignore */
      }
      // one-time start intent: consume it so a later metadata event (e.g. a
      // src reload) doesn't yank playback back to the old position.
      startAtMsRef.current = 0;
    }
    el.play().catch(() => {});
  };

  // Listen → Read: hand the reader the sentence currently being heard so it can
  // scroll+highlight it. anchor may be null (no timing) → reader keeps position.
  const switchToRead = () => {
    if (!onSwitchToRead) return;
    const sentences = stream && stream.timing && stream.timing.sentences;
    const anchor = anchorForMs(sentences || [], currentMs());
    // Switching TO reading → stop the audio (you're not listening anymore).
    if (nativePlaybackEnabled) sendNative('AUDIO_PAUSE');
    else if (audioRef.current) audioRef.current.pause();
    onPlaybackStateChange(false);
    setComposerOpen(false);
    setCommentText('');
    wasPlayingRef.current = false;
    setViewMode('reading');
    onViewModeChange('reading');
    onSwitchToRead(anchor);
    fadePlaybackHighlightForReading();
  };

  const listenFromHere = () => {
    clearHighlightFadeTimers();
    const sentences = stream?.timing?.sentences || [];
    const pos =
      requestedStartPositionRef.current || getPreferredTextPosition();
    requestedStartPositionRef.current = null;
    const keys =
      typeof orderedKeys === 'function' ? orderedKeys() : orderedKeys || [];
    const startMs =
      startMsForTextPosition(
        sentences,
        pos,
        keys,
        stream?.timing?.words || []
      ) || 0;

    setComposerOpen(false);
    setCommentText('');
    setViewMode('listening');
    onViewModeChange('listening');
    if (nativePlaybackEnabled) {
      nativeCurrentMsRef.current = startMs;
      setNativePlayback(previous => ({
        ...previous,
        currentMs: startMs,
      }));
      sendNative('AUDIO_SEEK', { positionMs: startMs });
      sendNative('AUDIO_PLAY');
    } else if (audioRef.current) {
      try {
        audioRef.current.currentTime = startMs / 1000;
      } catch (e) {
        /* keep the current position if the browser cannot seek yet */
      }
      setBrowserPlayback(previous => ({
        ...previous,
        currentMs: startMs,
      }));
      audioRef.current.play().catch(() => {});
    }
  };

  if (!enabled || !chapterId) return null;
  // no ready audio for this chapter → render nothing (no Listen affordance)
  if (!available && state === 'idle') return null;

  const words = stream && stream.timing && stream.timing.words;
  const playback = nativePlaybackEnabled ? nativePlayback : browserPlayback;
  const durationMs = playback.durationMs || stream?.durationMs || 0;
  const positionMs = Math.min(playback.currentMs, Math.max(0, durationMs));
  const isPlaying = playback.isPlaying;
  const chapterTitle = chapter?.title || bookTitle || 'Audiobook';
  const activeFeedbackAnchor =
    viewMode === 'reading'
      ? readingFeedbackSelection?.anchor
      : sentenceAnchorAtMs(stream?.timing?.sentences || [], positionMs);
  const activeFeedbackTargetKey = feedbackTargetKey(
    viewMode,
    activeFeedbackAnchor
  );
  const visibleReactions = recentReactions.filter(
    item => item.targetKey === activeFeedbackTargetKey
  );
  const reacted = reaction =>
    visibleReactions.some(item => item.reaction === reaction);
  const hasReadyStream = state === 'ready' && Boolean(stream);
  return (
    <div
      className={`br-audio-player${
        hasReadyStream ? ' br-audio-player--ready' : ''
      }${showBottomDock ? ' br-audio-player--docked' : ''}`}
      data-color-mode={colorMode}
      data-testid="chapter-audio-player"
    >
      {state === 'error' && (
        <div>
          <span>Couldn’t load the audio.</span>{' '}
          <button
            type="button"
            className="br-audio-retry"
            onClick={startListening}
          >
            Try loading audio again
          </button>
        </div>
      )}
      {state === 'unavailable' && (
        <span>Audio isn’t ready for this chapter.</span>
      )}
      {hasReadyStream && (
        <div className="br-audio-ready-content">
          {!nativePlaybackEnabled && (
            <audio
              ref={audioRef}
              data-testid="chapter-audio-el"
              onLoadedMetadata={handleLoadedMetadata}
              src={stream.url}
              preload="metadata"
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => {
                setBrowserPlayback(previous => ({
                  ...previous,
                  isPlaying: true,
                }));
                onPlaybackStateChange(true);
              }}
              onPause={() => {
                setBrowserPlayback(previous => ({
                  ...previous,
                  isPlaying: false,
                }));
                onPlaybackStateChange(false);
              }}
              onEnded={() => {
                setBrowserPlayback(previous => ({
                  ...previous,
                  isPlaying: false,
                }));
                onPlaybackStateChange(false);
                if (listenTrackerRef.current) {
                  emit(listenTrackerRef.current.markEnded());
                }
                onEnded();
              }}
            />
          )}

          {words && words.length ? (
            <p className="br-audio-karaoke" data-testid="karaoke">
              {words.map((w, i) => (
                <span
                  key={i}
                  className={
                    i === wordIndex
                      ? 'br-audio-word br-audio-word--active'
                      : 'br-audio-word'
                  }
                  style={
                    i === wordIndex
                      ? { backgroundColor: '#ffe08a', borderRadius: 3 }
                      : undefined
                  }
                >
                  {w.text}{' '}
                </span>
              ))}
            </p>
          ) : (
            <div className="br-audio-no-timing" data-testid="no-timing">
              Read-along highlight isn’t available for this chapter.
            </div>
          )}

        </div>
      )}

      {showBottomDock && (
        <section
          ref={dockRef}
          className={`br-audio-player-shell${
            viewMode === 'reading' ? ' br-audio-player-shell--reading' : ''
          }`}
          data-testid="audio-bottom-player"
          aria-label="Audiobook player"
        >
            {viewMode === 'reading' ? (
              <div className="br-audio-reading-mini">
                <button
                  type="button"
                  className="br-audio-listen-from-here"
                  data-testid="listen-from-here"
                  onPointerDown={captureRequestedStartPosition}
                  onClick={hasReadyStream ? listenFromHere : startListening}
                  disabled={state === 'loading'}
                >
                  <span aria-hidden="true">🎧</span>{' '}
                  {state === 'loading' ? 'Laddar ljud…' : 'Lyssna härifrån'}
                </button>
              </div>
            ) : (
              <div
                className="br-native-audio-controls br-audio-player-main"
                data-testid={
                  nativePlaybackEnabled ? 'native-audio-controls' : undefined
                }
              >
                <button
                  type="button"
                  className={`br-audio-play-toggle${
                    isPlaying ? ' br-audio-play-toggle--playing' : ''
                  }`}
                  data-testid={
                    nativePlaybackEnabled
                      ? 'native-play-toggle'
                      : 'browser-play-toggle'
                  }
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={() => {
                    if (nativePlaybackEnabled) {
                      sendNative(isPlaying ? 'AUDIO_PAUSE' : 'AUDIO_PLAY');
                    } else if (audioRef.current) {
                      if (isPlaying) audioRef.current.pause();
                      else audioRef.current.play().catch(() => {});
                    }
                  }}
                >
                  <span className="br-audio-play-icon" aria-hidden="true" />
                  <span className="br-audio-visually-hidden">
                    {isPlaying ? 'Pause' : 'Play'}
                  </span>
                </button>
                <div className="br-audio-track-block">
                  <div className="br-audio-track-heading">
                    <strong title={chapterTitle}>{chapterTitle}</strong>
                    {onSwitchToRead && (
                      <button
                        type="button"
                        className="br-audio-switch-read"
                        data-testid="switch-to-read"
                        onClick={switchToRead}
                      >
                        <span aria-hidden="true">📖</span> Läs härifrån
                      </button>
                    )}
                  </div>
                  <input
                    className="br-audio-position"
                    type="range"
                    aria-label="Audiobook position"
                    min="0"
                    max={Math.max(1, durationMs)}
                    value={positionMs}
                    style={{
                      '--br-audio-progress': `${
                        durationMs ? (positionMs / durationMs) * 100 : 0
                      }%`,
                    }}
                    onChange={event => {
                      const nextPositionMs = Number(event.target.value);
                      if (nativePlaybackEnabled) {
                        nativeCurrentMsRef.current = nextPositionMs;
                        setNativePlayback(previous => ({
                          ...previous,
                          currentMs: nextPositionMs,
                        }));
                        sendNative('AUDIO_SEEK', {
                          positionMs: nextPositionMs,
                        });
                      } else if (audioRef.current) {
                        audioRef.current.currentTime = nextPositionMs / 1000;
                        setBrowserPlayback(previous => ({
                          ...previous,
                          currentMs: nextPositionMs,
                        }));
                      }
                    }}
                  />
                  <div className="br-audio-time" aria-live="off">
                    {formatTime(positionMs)} / {formatTime(durationMs)}
                  </div>
                </div>
              </div>
            )}

            {viewMode === 'reading' && readingFeedbackSelection && (
              <div
                className="br-audio-reading-selection"
                data-testid="reading-selection-preview"
              >
                “{readingFeedbackSelection.text}”
              </div>
            )}

            {(viewMode === 'listening' || readingFeedbackSelection) && (
              <div
                className="br-audio-reactions"
                data-testid="reaction-row"
                aria-label={
                  viewMode === 'reading'
                    ? 'React to the selected text'
                    : 'React to the current sentence'
                }
              >
                <div
                  className="br-audio-reaction-label"
                  data-testid="reaction-feedback"
                  aria-live="polite"
                >
                  {visibleReactions.length > 0 ? (
                    <>
                      {visibleReactions.map(({ emoji, audioMs }, index) => (
                        <span key={`${audioMs}-${index}`}>{emoji}</span>
                      ))}{' '}
                      {viewMode === 'reading'
                        ? 'Reaction saved for the selected text'
                        : 'Reaction saved at this sentence'}
                    </>
                  ) : viewMode === 'reading' ? (
                    'React to the selected text'
                  ) : isPlaying ? (
                    'React now — audio keeps playing'
                  ) : (
                    'React to the highlighted sentence'
                  )}
                </div>
                <div className="br-audio-reaction-grid">
                  {REACTIONS.map(r => (
                    <button
                      key={r.reaction}
                      type="button"
                      className="br-audio-reaction"
                      aria-label={r.reaction}
                      aria-pressed={reacted(r.reaction)}
                      title={r.reaction}
                      onClick={() => handleReaction(r.reaction)}
                    >
                      {r.emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(viewMode === 'listening' || readingFeedbackSelection) && (
              <div className="br-audio-feedback">
                <div className="br-audio-feedback-label">
                  {viewMode === 'reading'
                    ? 'Comment on the selected text'
                    : isPlaying
                      ? 'Comment — audio pauses automatically'
                      : 'Audio is paused — comment on this sentence'}
                </div>
                {!composerOpen ? (
                  <button
                    type="button"
                    className="br-audio-comment-btn"
                    data-testid="comment-here"
                    onClick={openComposer}
                  >
                    <span aria-hidden="true">💬</span> Comment here
                  </button>
                ) : (
                  <div className="br-audio-composer" data-testid="composer">
                    <FeedbackOrigin value={feedbackOrigin} onChange={setFeedbackOrigin} />
                    <textarea
                      className="br-audio-composer-input"
                      data-testid="composer-input"
                      aria-label="Comment"
                      placeholder="What did you think of this line?"
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      autoFocus
                    />
                    <div className="br-audio-composer-actions">
                      <button
                        type="button"
                        className="br-audio-comment-submit"
                        disabled={feedbackOrigin !== 'personal' || !commentText.trim()}
                        data-testid="composer-submit"
                        onClick={submitComposer}
                      >
                        Comment
                      </button>
                      <button
                        type="button"
                        className="br-audio-comment-cancel"
                        data-testid="composer-cancel"
                        onClick={() => closeComposer()}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {viewMode === 'listening' && (
              <div className="br-audio-disclaimer">
                AI-narrated draft — for feedback, not a finished audiobook.
              </div>
            )}
        </section>
      )}
    </div>
  );
};

export default ChapterAudioPlayer;
